import { DiagramType } from '../tools/interfaces/ITool';

export interface IFrameBounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface IFrameToolConfig {
  allowedTools?: string[];
  disabledTools?: string[];
  defaultTools?: string[];
  toolOverrides?: Record<string, any>;
}

export interface IFrame {
  id: string;
  name: string;
  type: DiagramType;
  /** Optional version for frame definition. Defaults to 1. */
  version?: number;
  bounds: IFrameBounds;
  shapes: Set<string>;
  connectors: Set<string>;
  childFrames?: Set<string>;
  parentFrame?: string;
  
  // Visual properties
  visible?: boolean;
  locked?: boolean;
  backgroundColor?: string;
  borderColor?: string;
  borderWidth?: number;
  opacity?: number;
  
  // Frame-specific tool configuration
  toolConfig?: IFrameToolConfig;
  
  // Metadata for domain-specific information
  metadata: Record<string, any>;
  
  // Methods
  containsPoint(x: number, y: number): boolean;
  containsShape(shapeId: string): boolean;
  containsConnector(connectorId: string): boolean;
  addShape(shapeId: string): void;
  removeShape(shapeId: string): void;
  addConnector(connectorId: string): void;
  removeConnector(connectorId: string): void;
  getBounds(): IFrameBounds;
  setBounds(bounds: Partial<IFrameBounds>): void;
  addChildFrame(frameId: string): void;
  removeChildFrame(frameId: string): void;
  setParentFrame(frameId: string | undefined): void;
  toJSON(): any;
}

export class Frame implements IFrame {
  id: string;
  name: string;
  type: DiagramType;
  version?: number;
  bounds: IFrameBounds;
  shapes: Set<string>;
  connectors: Set<string>;
  childFrames?: Set<string>;
  parentFrame?: string;
  visible: boolean;
  locked: boolean;
  backgroundColor?: string;
  borderColor?: string;
  borderWidth?: number;
  opacity?: number;
  toolConfig?: IFrameToolConfig;
  metadata: Record<string, any>;
  
  constructor(config: {
    id: string;
    name: string;
    type: DiagramType;
    version?: number;
    bounds: IFrameBounds;
    metadata?: Record<string, any>;
    toolConfig?: IFrameToolConfig;
    visible?: boolean;
    locked?: boolean;
    backgroundColor?: string;
    borderColor?: string;
    borderWidth?: number;
    opacity?: number;
  }) {
    this.id = config.id;
    this.name = config.name;
    this.type = config.type;
    this.version = config.version;
    this.bounds = { ...config.bounds };
    this.shapes = new Set();
    this.connectors = new Set();
    this.childFrames = new Set();
    this.metadata = config.metadata || {};
    this.toolConfig = config.toolConfig;
    this.visible = config.visible ?? true;
    this.locked = config.locked ?? false;
    this.backgroundColor = config.backgroundColor;
    this.borderColor = config.borderColor;
    this.borderWidth = config.borderWidth;
    this.opacity = config.opacity;
  }
  
  containsPoint(x: number, y: number): boolean {
    return x >= this.bounds.x &&
           x <= this.bounds.x + this.bounds.width &&
           y >= this.bounds.y &&
           y <= this.bounds.y + this.bounds.height;
  }
  
  containsShape(shapeId: string): boolean {
    return this.shapes.has(shapeId);
  }
  
  containsConnector(connectorId: string): boolean {
    return this.connectors.has(connectorId);
  }
  
  addShape(shapeId: string): void {
    this.shapes.add(shapeId);
  }
  
  removeShape(shapeId: string): void {
    this.shapes.delete(shapeId);
  }
  
  addConnector(connectorId: string): void {
    this.connectors.add(connectorId);
  }
  
  removeConnector(connectorId: string): void {
    this.connectors.delete(connectorId);
  }
  
  getBounds(): IFrameBounds {
    return { ...this.bounds };
  }
  
  setBounds(bounds: Partial<IFrameBounds>): void {
    this.bounds = { ...this.bounds, ...bounds };
  }
  
  updateMetadata(metadata: Record<string, any>): void {
    this.metadata = { ...this.metadata, ...metadata };
  }
  
  getMetadata(key?: string): any {
    if (key) {
      return this.metadata[key];
    }
    return { ...this.metadata };
  }
  
  setToolConfig(config: IFrameToolConfig): void {
    this.toolConfig = config;
  }
  
  isToolAllowed(toolId: string): boolean {
    if (!this.toolConfig) return true;
    
    if (this.toolConfig.disabledTools?.includes(toolId)) {
      return false;
    }
    
    if (this.toolConfig.allowedTools) {
      return this.toolConfig.allowedTools.includes(toolId);
    }
    
    return true;
  }
  
  addChildFrame(frameId: string): void {
    if (!this.childFrames) {
      this.childFrames = new Set();
    }
    this.childFrames.add(frameId);
  }
  
  removeChildFrame(frameId: string): void {
    this.childFrames?.delete(frameId);
  }
  
  setParentFrame(frameId: string | undefined): void {
    this.parentFrame = frameId;
  }
  
  toJSON(): any {
    return {
      id: this.id,
      name: this.name,
      type: this.type,
      version: this.version,
      bounds: this.bounds,
      shapes: Array.from(this.shapes),
      connectors: Array.from(this.connectors),
      childFrames: this.childFrames ? Array.from(this.childFrames) : undefined,
      parentFrame: this.parentFrame,
      visible: this.visible,
      locked: this.locked,
      backgroundColor: this.backgroundColor,
      borderColor: this.borderColor,
      borderWidth: this.borderWidth,
      opacity: this.opacity,
      toolConfig: this.toolConfig,
      metadata: this.metadata
    };
  }
  
  static fromJSON(json: any): Frame {
    const frame = new Frame({
      id: json.id,
      name: json.name,
      type: json.type,
      version: json.version,
      bounds: json.bounds,
      metadata: json.metadata,
      toolConfig: json.toolConfig,
      visible: json.visible,
      locked: json.locked,
      backgroundColor: json.backgroundColor,
      borderColor: json.borderColor,
      borderWidth: json.borderWidth,
      opacity: json.opacity
    });
    
    if (json.shapes) {
      json.shapes.forEach((shapeId: string) => frame.addShape(shapeId));
    }
    
    if (json.connectors) {
      json.connectors.forEach((connectorId: string) => frame.addConnector(connectorId));
    }
    
    if (json.childFrames) {
      json.childFrames.forEach((frameId: string) => frame.addChildFrame(frameId));
    }
    
    if (json.parentFrame) {
      frame.setParentFrame(json.parentFrame);
    }
    
    return frame;
  }
}
